import { useState, useMemo, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import {
  Search,
  Users,
  UserPlus,
  Activity,
  MoreHorizontal,
  LogIn,
  Eye,
  ChevronLeft,
  ChevronRight,
  AlertCircle,
  RefreshCw,
} from 'lucide-react'
import { Button } from '../components/ui/button'
import { Input } from '../components/ui/input'
import { Select } from '../components/ui/select'
import { Badge } from '../components/ui/badge'
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card'
import { Skeleton } from '../components/ui/skeleton'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table'
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../components/ui/dropdown-menu'
import { useAdminUsers, useAdminUserStats, useImpersonateUser } from '../hooks/useAdmin'
import { useAdminStore } from '../stores/adminStore'
import { toast } from '../hooks/useToast'
import { getApiError } from '../lib/api'
import type { GetUsersParams } from '../types/admin'

const PAGE_SIZE = 20

function useDebounce<T>(value: T, delay: number): T {
  const [debouncedValue, setDebouncedValue] = useState<T>(value)

  useEffect(() => {
    const handler = setTimeout(() => {
      setDebouncedValue(value)
    }, delay)
    return () => clearTimeout(handler)
  }, [value, delay])

  return debouncedValue
}

export default function AdminUsers() {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const impersonateMutation = useImpersonateUser()
  const startImpersonation = useAdminStore((s) => s.startImpersonation)

  const [searchQuery, setSearchQuery] = useState('')
  const [roleFilter, setRoleFilter] = useState<string>('all')
  const [page, setPage] = useState(0)

  const debouncedSearch = useDebounce(searchQuery, 300)

  const queryParams = useMemo((): GetUsersParams => {
    const params: GetUsersParams = {
      skip: page * PAGE_SIZE,
      limit: PAGE_SIZE,
    }
    if (roleFilter !== 'all') {
      params.role = roleFilter
    }
    if (debouncedSearch) {
      params.search = debouncedSearch
    }
    return params
  }, [roleFilter, debouncedSearch, page])

  const { data, isLoading, isFetching, error, refetch } = useAdminUsers(queryParams)
  const { data: stats, isLoading: statsLoading } = useAdminUserStats()

  const users = data?.users ?? []
  const total = data?.total ?? 0

  const totalPages = Math.ceil(total / PAGE_SIZE)
  const hasNextPage = page + 1 < totalPages
  const hasPrevPage = page > 0
  const showingFrom = total === 0 ? 0 : page * PAGE_SIZE + 1
  const showingTo = Math.min((page + 1) * PAGE_SIZE, total)

  const handleRoleFilterChange = (value: string) => {
    setRoleFilter(value)
    setPage(0)
  }

  const handleImpersonate = async (userId: number) => {
    try {
      const result = await impersonateMutation.mutateAsync(userId)
      startImpersonation(result.user, result.token)
    } catch (err) {
      const apiError = getApiError(err)
      toast({
        variant: 'destructive',
        title: t('common.error'),
        description: apiError.message,
      })
    }
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('es-ES', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
    })
  }

  if (error) {
    const apiError = getApiError(error)
    return (
      <div className="flex flex-col items-center justify-center py-12 text-center">
        <AlertCircle className="h-12 w-12 text-destructive mb-4" />
        <h3 className="text-lg font-semibold">{t('admin.errorLoading')}</h3>
        <p className="text-muted-foreground mb-4">{apiError.message}</p>
        <Button onClick={() => refetch()}>
          <RefreshCw className="mr-2 h-4 w-4" />
          {t('common.retry')}
        </Button>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold tracking-tight">{t('admin.users.title')}</h1>
        <p className="text-muted-foreground">{t('admin.users.subtitle')}</p>
      </div>

      {/* Stats Cards */}
      <div className="grid gap-4 sm:grid-cols-3">
        {statsLoading ? (
          <>
            {[...Array(3)].map((_, i) => (
              <Card key={i}>
                <CardHeader className="pb-2">
                  <Skeleton className="h-4 w-24 mb-2" />
                  <Skeleton className="h-8 w-16" />
                </CardHeader>
              </Card>
            ))}
          </>
        ) : (
          <>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardDescription>{t('admin.users.stats.totalUsers')}</CardDescription>
                <Users className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <CardTitle className="text-2xl">{stats?.totalUsers ?? 0}</CardTitle>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardDescription>{t('admin.users.stats.newThisMonth')}</CardDescription>
                <UserPlus className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <CardTitle className="text-2xl">{stats?.newThisMonth ?? 0}</CardTitle>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="flex flex-row items-center justify-between pb-2">
                <CardDescription>{t('admin.users.stats.activeUsers')}</CardDescription>
                <Activity className="h-4 w-4 text-muted-foreground" />
              </CardHeader>
              <CardContent>
                <CardTitle className="text-2xl">{stats?.activeUsers ?? 0}</CardTitle>
              </CardContent>
            </Card>
          </>
        )}
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col gap-2 sm:flex-row">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder={t('admin.users.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => {
                  setSearchQuery(e.target.value)
                  setPage(0)
                }}
                className="pl-9"
              />
            </div>
            <Select
              value={roleFilter}
              onChange={(e) => handleRoleFilterChange(e.target.value)}
              className="sm:w-[180px]"
            >
              <option value="all">{t('common.all')}</option>
              <option value="client">{t('admin.users.roles.client')}</option>
              <option value="admin">{t('admin.users.roles.admin')}</option>
            </Select>
          </div>
        </CardContent>
      </Card>

      {/* Users Table */}
      <Card>
        <CardContent className="p-0">
          {isLoading ? (
            <div className="p-6 space-y-4">
              {[...Array(5)].map((_, i) => (
                <div key={i} className="flex items-center gap-4">
                  <Skeleton className="h-12 flex-1" />
                  <Skeleton className="h-12 w-40" />
                  <Skeleton className="h-12 w-24" />
                  <Skeleton className="h-12 w-24" />
                  <Skeleton className="h-12 w-12" />
                </div>
              ))}
            </div>
          ) : users.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Users className="h-12 w-12 text-muted-foreground/50 mb-4" />
              <h3 className="text-lg font-semibold">{t('admin.users.noUsers')}</h3>
            </div>
          ) : (
            <>
              <div className="relative">
                {isFetching && !isLoading && (
                  <div className="absolute inset-0 bg-background/50 flex items-center justify-center z-10">
                    <RefreshCw className="h-6 w-6 animate-spin text-muted-foreground" />
                  </div>
                )}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.users.table.name')}</TableHead>
                      <TableHead>{t('admin.users.table.email')}</TableHead>
                      <TableHead>{t('admin.users.table.role')}</TableHead>
                      <TableHead className="hidden md:table-cell">{t('admin.users.table.invoices')}</TableHead>
                      <TableHead className="hidden md:table-cell">{t('admin.users.table.registered')}</TableHead>
                      <TableHead className="w-[70px]"></TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user.id}>
                        <TableCell>
                          <button
                            className="font-medium text-left hover:underline cursor-pointer"
                            onClick={() => navigate(`/dashboard/admin/users/${user.id}`)}
                          >
                            {user.name}
                          </button>
                        </TableCell>
                        <TableCell>{user.email}</TableCell>
                        <TableCell>
                          <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                            {t(`admin.users.roles.${user.role}`)}
                          </Badge>
                        </TableCell>
                        <TableCell className="hidden md:table-cell">{user.invoiceCount}</TableCell>
                        <TableCell className="hidden md:table-cell">{formatDate(user.createdAt)}</TableCell>
                        <TableCell>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <Button variant="ghost" size="icon">
                                <MoreHorizontal className="h-4 w-4" />
                              </Button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem
                                onClick={() => navigate(`/dashboard/admin/users/${user.id}`)}
                              >
                                <Eye className="mr-2 h-4 w-4" />
                                {t('admin.users.openCard')}
                              </DropdownMenuItem>
                              {user.role !== 'admin' && (
                                <DropdownMenuItem
                                  onClick={() => handleImpersonate(user.id)}
                                  disabled={impersonateMutation.isPending}
                                >
                                  <LogIn className="mr-2 h-4 w-4" />
                                  {t('admin.users.openCabinet')}
                                </DropdownMenuItem>
                              )}
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-4 py-4 border-t">
                <div className="text-sm text-muted-foreground">
                  {t('admin.users.showing', { from: showingFrom, to: showingTo, total })}
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p - 1)}
                    disabled={!hasPrevPage || isFetching}
                  >
                    <ChevronLeft className="h-4 w-4 mr-1" />
                    {t('common.previous')}
                  </Button>
                  <span className="text-sm text-muted-foreground">
                    {page + 1} / {totalPages || 1}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => p + 1)}
                    disabled={!hasNextPage || isFetching}
                  >
                    {t('common.next')}
                    <ChevronRight className="h-4 w-4 ml-1" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  )
}
